import React, { useCallback, useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useWithdrawBond } from '../hooks/useWithdrawBond';
import { useTokenInfo } from '../../../../hooks/useTokenInfo';
import useWallet from '../../../../hooks/useWallet';
import Loader from '../../../../shared/Loader';
import TokenToDollor from '../../../../shared/TokenToDollor';
import { useTheme } from '../../../../hooks/ThemeProvider';
import { EXPLORER_URL } from '../../../../utils/constant';

function WithdrawBondModal({ show, onHide, poolData, setDummyData }) {
    const { address } = useWallet();
    const [currentStep, setCurrentStep] = useState(1);
    const [transactionHash, setTransactionHash] = useState('');
    const [gasEstimation, setGasEstimation] = useState({
        totalGasCostETH: 0,
        totalGasCostETHFormatted: "0"
    });
    const [bondAmount, setBondAmount] = useState({
        bondAmountETH: "0",
        bondAmountNumber: 0
    });

    const { symbol } = useTokenInfo(poolData?.rootPool?.metadata?.rewardToken);
    const { withdrawBondAmount, estimateWithdrawBondGas, isWithdrawing, getBondAmount, isLoadingBondAmount } = useWithdrawBond();

    const shortenAddress = (address) => {
        if (!address) return 'N/A';
        return `${address.slice(0, 6)}...${address.slice(-4)}`;
    };

    const truncateHash = (hash) => {
        if (!hash) return "0x74rhiw4rf38u...5r2";
        return `${hash.slice(0, 10)}...${hash.slice(-3)}`;
    };

    const handleClose = () => {
        setCurrentStep(1);
        setTransactionHash('');
        onHide();
    };

    const handleWithdraw = async () => {
        if (!bondAmount?.bondAmountNumber || bondAmount.bondAmountNumber === 0) {
            toast.error('No bond amount available to withdraw');
            return;
        }

        try {
            const { receipt, hash } = await withdrawBondAmount(poolData.contractAddress);

            if (receipt.status === 'success') {
                setTransactionHash(hash);
                setCurrentStep(2);
            } else {
                toast.error('Transaction failed. Please try again.');
            }
        } catch (error) {
            console.error('Error withdrawing bond:', error);

            if (error.message.includes('user rejected')) {
                toast.error('Transaction cancelled by user');
            } else if (error.message.includes('insufficient funds')) {
                toast.error('Insufficient funds for gas fees');
            } else {
                toast.error('Failed to withdraw bond. Please try again.');
            }
        }
    };

    const fetchBondAmount = useCallback(async () => {
        if (!poolData?.contractAddress) return;

        try {
            const bondData = await getBondAmount(poolData.contractAddress);
            setBondAmount({
                bondAmountETH: bondData?.bondAmountETH || "0",
                bondAmountNumber: bondData?.bondAmountNumber || 0
            });
        } catch (error) {
            console.error("Failed to fetch bond amount:", error);
        }
    }, [poolData?.contractAddress, getBondAmount]);

    const fetchGasEstimation = useCallback(async () => {
        if (!poolData?.contractAddress) return;

        try {
            const gasData = await estimateWithdrawBondGas(poolData.contractAddress);
            setGasEstimation({
                totalGasCostETH: gasData?.totalGasCostETH || 0,
                totalGasCostETHFormatted: gasData?.totalGasCostETHFormatted || "0"
            });
        } catch (error) {
            console.error("Failed to fetch gas estimation:", error);
        }
    }, [poolData?.contractAddress, estimateWithdrawBondGas]);

    useEffect(() => {
        if (poolData?.contractAddress && show) {
            fetchBondAmount();
            fetchGasEstimation();
        }
    }, [poolData?.contractAddress, show, fetchBondAmount, fetchGasEstimation]);

    const isDisabled = !bondAmount?.bondAmountNumber ||
        bondAmount.bondAmountNumber === 0 ||
        isWithdrawing ||
        isLoadingBondAmount;

    const getLoadingText = () => {
        if (isWithdrawing) return 'Processing Bond Withdrawal...';
        if (isLoadingBondAmount) return 'Loading Bond Amount...';
        return 'Loading...';
    };

    const isLoading = isWithdrawing || isLoadingBondAmount;
 const { theme } = useTheme();
    return (
        <>
            {isLoading && <Loader loadingText={getLoadingText()} />}

            {/* Step 1: Withdraw Bond Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 1 && !isLoading}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Withdraw Bond Amount</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="modal_share">
                        <div className="inner_type">
                            <div className="one_modal">
                                <p>Bond Amount</p>
                                <h3>{bondAmount?.bondAmountETH || 0} {symbol}</h3>
                                <p className="numbers">~<TokenToDollor
                                    tokenAddress={poolData?.rootPool?.metadata?.rewardToken}
                                    tokenAmount={bondAmount?.bondAmountETH || '0'}
                                /></p>
                            </div>
                            <div className="main_lock">
                                <div className="inner_lock">
                                    <p>Pool Address</p>
                                    <span>{poolData ? shortenAddress(poolData.contractAddress) : 'N/A'}</span>
                                </div>
                                <div className="inner_lock">
                                    <p>Creator</p>
                                    <span>{shortenAddress(poolData?.creator?.walletAddress) || 'Unknown'}</span>
                                </div>
                                <div className="inner_lock">
                                    <p>Gas Fees</p>
                                    <span>
                                        <TokenToDollor
                                            tokenAddress={''}
                                            tokenAmount={gasEstimation.totalGasCostETHFormatted || '0'}
                                        /> ({gasEstimation.totalGasCostETHFormatted} ETH)
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="buton_modal_div">
                            <button
                                onClick={handleWithdraw}
                                disabled={isDisabled}
                                style={{
                                    opacity: isDisabled ? 0.5 : 1,
                                    cursor: isDisabled ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {isWithdrawing ? 'Processing...' : 'Withdraw Bond'}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Step 2: Success Modal */}
            <Modal
                className="stakingmodal"
                show={show && currentStep === 2 && !isLoading}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Success</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="succes_modal">
                        <div className="top_success">
                            <img src="/assets/tick.svg" alt="success" />
                            <h3>Bond Withdrawn</h3>
                            <p>
                                You’ve successfully withdrawn your bond amount - funds are now available.
                            </p>
                        </div>
                        <div className="hash_div">
                            <div>
                                <p>Transaction Hash</p>
                                <span>{truncateHash(transactionHash)}</span>
                            </div>
                            <button
                                onClick={() => window.open(`${EXPLORER_URL}${transactionHash}`, '_blank')}
                            >
 {theme === 'dark' ? (
                            <img src="/assets/whitetab.svg" alt="view" />
                    ) : (
                     <svg xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15" fill="none">
  <path d="M12.25 8.08333V11.5833C12.25 11.8928 12.1271 12.1895 11.9083 12.4083C11.6895 12.6271 11.3928 12.75 11.0833 12.75H2.91667C2.60725 12.75 2.3105 12.6271 2.09171 12.4083C1.87292 12.1895 1.75 11.8928 1.75 11.5833V3.41667C1.75 3.10725 1.87292 2.8105 2.09171 2.59171C2.3105 2.37292 2.60725 2.25 2.91667 2.25H6.41667" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M12.25 2.25L7 7.5" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.75 2.25H12.25V5.75" stroke="#000F2F" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                    )}
                                View
                            </button>
                        </div>
                        <div className="buton_modal_div">
                            <button onClick={handleClose}>Close</button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default WithdrawBondModal;